/**
 * Question-related type definitions for Insights Hub
 *
 * This file contains all the TypeScript interfaces, types, and enums
 * related to questions in the Insights Hub feature, matching the backend API.
 */

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Question categories for classification based on backend database
 */
export enum QuestionCategory {
  SALES_OVERVIEW = 'sales_overview',
  INVENTORY_FORECASTING = 'inventory_forecasting',
  CUSTOMER_INSIGHTS = 'customer_insights',
  PRODUCT_ANALYTICS = 'product_analytics',
  FINANCIAL_REPORTING = 'financial_reporting',
  OPERATIONS_MONITORING = 'operations_monitoring',
  ORDER_ANALYTICS = 'order_analytics',
  MARKETING_ANALYTICS = 'marketing_analytics',
  SUPPLIER_ANALYTICS = 'supplier_analytics',
  GENERAL_QUERY = 'general',
  CUSTOM = 'custom',
}

/**
 * Question status values
 */
export enum QuestionStatus {
  ACTIVE = 'active',
  DELETED = 'deleted',
}

/**
 * Question priority levels for pinned questions
 */
export enum QuestionPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

/**
 * Question source types
 */
export enum QuestionSource {
  PREDEFINED = 'predefined',
  CUSTOM = 'custom',
  RECENT_CLICK = 'recent_click',
  PINNED_CLICK = 'pinned_click',
}

/**
 * Pin reasons for pinned questions
 */
export enum PinReason {
  FREQUENTLY_USED = 'frequently_used',
  USER_PREFERENCE = 'user_preference',
  RECOMMENDED = 'recommended',
  CRITICAL_METRIC = 'critical_metric',
  CUSTOM = 'custom',
}

/**
 * Data freshness requirements
 */
export enum DataFreshness {
  REAL_TIME = 'real_time',
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

// =============================================================================
// INTERFACES - Matching Backend Database Schema
// =============================================================================

/**
 * Base question interface matching backend database schema
 */
export interface IBaseQuestion {
  /** Unique question identifier */
  id: number;

  /** The question text */
  question_text: string;

  /** Instruction text for execution */
  instruction: string;

  /** SHA-256 hash for duplicate detection */
  question_hash: string;

  /** Measure code for analytics */
  measure_code: string;

  /** Optional dimension code */
  dimension_code?: string;

  /** User ID (optional) */
  user_id?: number;

  /** Merchant ID (required) */
  merchant_id: number;

  /** Question status */
  status: QuestionStatus;

  /** Question source */
  source: QuestionSource;

  /** Number of times executed */
  execution_count: number;

  /** When created */
  created_at: string;

  /** When last updated */
  updated_at: string;

  /** When last executed */
  last_executed_at?: string;
}

/**
 * Question metadata for enhanced functionality (stored as JSON in DB)
 */
export interface IQuestionMetadata {
  /** Source of the question (web, mobile, api) */
  source?: string;

  /** Detected user intent */
  detected_intent?: string;

  /** Question complexity score (0-1) */
  complexity_score?: number;

  /** User agent string */
  user_agent?: string;

  /** Session identifier */
  session_id?: string;

  /** Question tags for categorization */
  tags?: string[];

  /** Auto-refresh configuration */
  auto_refresh?: boolean;

  /** Refresh interval in hours */
  refresh_interval_hours?: number;

  /** Data freshness requirement */
  data_freshness?: DataFreshness;

  /** Icon for UI display */
  icon?: string;

  /** Color for UI display */
  color?: string;
}

/**
 * Question analytics data
 */
export interface IQuestionAnalytics {
  /** Average execution time in ms */
  avg_execution_time: number;

  /** Success rate percentage */
  success_rate: number;

  /** Total execution count */
  total_executions: number;

  /** Last 7 days execution count */
  last_7_days_executions: number;

  /** Last 30 days execution count */
  last_30_days_executions: number;

  /** User satisfaction score */
  user_satisfaction?: number;

  /** Common follow-up questions */
  follow_up_questions?: string[];
}

/**
 * Recent question interface matching backend recent_questions table
 */
export interface IRecentQuestion extends IBaseQuestion {
  /** Metadata stored as JSON */
  metadata?: IQuestionMetadata;

  /** Analytics data if available */
  analytics?: IQuestionAnalytics;
}

/**
 * Pinned question interface matching backend pinned_questions table
 */
export interface IPinnedQuestion extends IBaseQuestion {
  /** Category */
  category?: string;

  /** Priority level */
  priority: QuestionPriority;

  /** Display order for UI */
  display_order: number;

  /** Pin reason */
  pin_reason?: string;

  /** Pin context */
  pin_context?: string;

  /** When pinned */
  pinned_at?: string;

  /** Metadata stored as JSON */
  metadata?: IQuestionMetadata;

  /** Analytics data */
  analytics?: IQuestionAnalytics;
}

// =============================================================================
// REQUEST/RESPONSE INTERFACES
// =============================================================================

/**
 * Request interface for creating recent questions
 */
export interface ICreateRecentQuestionRequest {
  /** The question text */
  question_text: string;

  /** Instruction for execution */
  instruction: string;

  /** Measure code */
  measure_code: string;

  /** Optional dimension code */
  dimension_code?: string;

  /** Question source */
  source?: QuestionSource;

  /** Optional metadata */
  metadata?: Partial<IQuestionMetadata>;

  /** Category */
  category?: QuestionCategory | string;
}

/**
 * Request interface for fetching recent questions
 */
export interface IFetchRecentQuestionsRequest {
  /** Filter by category */
  category?: QuestionCategory | string;

  /** Search in question text (min 3 chars) */
  search?: string;

  /** Filter by pinned status */
  is_pinned?: boolean;

  /** Filter from date (ISO format) */
  date_from?: string;

  /** Filter to date (ISO format) */
  date_to?: string;

  /** Minimum execution count */
  min_execution_count?: number;

  /** Maximum execution count */
  max_execution_count?: number;

  /** Include metadata in response */
  include_metadata?: boolean;

  /** Sort field */
  sort_by?: 'created_at' | 'updated_at' | 'last_executed_at' | 'execution_count';

  /** Sort order */
  sort_order?: 'asc' | 'desc';

  /** Pagination limit */
  limit?: number;

  /** Pagination offset */
  offset?: number;
}

/**
 * Request interface for creating pinned questions
 */
export interface ICreatePinnedQuestionRequest {
  /** The question text */
  question_text: string;

  /** Instruction for execution */
  instruction: string;

  /** Measure code */
  measure_code: string;

  /** Optional dimension code */
  dimension_code?: string;

  /** Category */
  category?: string;

  /** Priority level */
  priority?: QuestionPriority;

  /** Pin reason */
  pin_reason?: string;

  /** Pin context */
  pin_context?: string;

  /** Optional metadata */
  metadata?: Partial<IQuestionMetadata>;
}

/**
 * Request interface for fetching pinned questions
 */
export interface IFetchPinnedQuestionsRequest {
  /** Filter by priority */
  priority?: QuestionPriority;

  /** Filter by category */
  category?: QuestionCategory | string;

  /** Filter by pin reason */
  pin_reason?: string;

  /** Include analytics data */
  include_analytics?: boolean;

  /** Pagination limit */
  limit?: number;

  /** Pagination offset */
  offset?: number;
}

/**
 * Request interface for bulk reordering
 */
export interface IBulkReorderRequest {
  /** Array of order updates */
  order_updates: Array<{
    id: number;
    display_order: number;
  }>;
}

/**
 * Question execution statistics
 */
export interface IQuestionExecutionStats {
  /** Total questions count */
  total_questions: number;

  /** Questions by category */
  questions_by_measure: Record<string, number>;

  /** Questions by day */
  questions_by_day: Record<string, number>;

  /** Most popular questions */
  most_popular_questions: IRecentQuestion[];
}

/**
 * Pinned questions summary
 */
export interface IPinnedQuestionsSummary {
  /** Total pinned count */
  total_pinned: number;

  /** Pinned by category */
  by_category: Record<string, number>;

  /** Pinned by priority */
  by_priority: Record<QuestionPriority, number>;

  /** Most executed pinned questions */
  most_executed: IPinnedQuestion[];
}

/**
 * Pinned quota information
 */
export interface IPinnedQuota {
  /** Maximum allowed pinned questions */
  limit: number;

  /** Currently used count */
  used: number;

  /** Remaining available slots */
  remaining: number;
}

/**
 * Question category statistics
 */
export interface IQuestionCategoryStats {
  /** Category name */
  category: QuestionCategory;

  /** Total questions in this category */
  total_questions: number;

  /** Recent questions count */
  recent_count: number;

  /** Pinned questions count */
  pinned_count: number;

  /** Average execution count */
  avg_execution_count: number;

  /** Most popular question in this category */
  most_popular_question?: IRecentQuestion;
}
